import { useState, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import type { Zone } from '@vehix/portal/services/api/tenants';
import { getZoneSizeLabel } from '../utils/zoneStatistics';
import {
  ASSIGNED_ZONE_COLOR,
  AVAILABLE_ZONE_COLOR,
  ZONE_AVAILABILITY_BADGE,
  getOemHexColor
} from '../utils/oemColors';

/**
 * Captures the Zone Map (in Presentation mode) as a customer-shareable PNG.
 *
 * Layout: the live Leaflet viewport stacks on top of a generated legend
 * strip listing each selected zone with its OEM color swatch, size, and
 * recall counts. A footer line carries the generation date and product
 * name so the artifact is self-describing without surrounding context.
 *
 * Why a custom composite rather than a single html2canvas call on the
 * whole page: the floating filter overlay would otherwise appear in the
 * exported PNG. The map div is captured in isolation; the legend strip is
 * built fresh as off-screen DOM so it always reflects the current
 * selection without depending on a real DOM node living somewhere on the
 * page.
 *
 * `crossOrigin='anonymous'` on the OSM TileLayer is what makes the canvas
 * non-tainted — without it, `canvas.toBlob` would refuse to produce a PNG
 * for security reasons. See ZoneDrawingMapOptimized.tsx tile setup.
 */
export interface ExportPngOptions {
  /** Zones to render in the legend strip (typically the user's selection). */
  zones: Zone[];
  /** zone_id → current recall count, paired with `zones` by id. */
  recallCountsNow?: Record<string, number>;
  /**
   * zone_id → the crawl cutoff that zone's count was taken at.
   *
   * The footer reports the OLDEST of these as the data date. Counts are tenant-scoped and
   * taken as of a per-tenant crawl, which can lag today by weeks — so a PNG footer that
   * only said "Generated <today>" invited a dealer to read today's date as the data date.
   * The oldest is used rather than the newest because this artifact leaves the building:
   * it must not claim to be fresher than its stalest number.
   */
  crawlDatesNow?: Record<string, { latest?: string; oldest?: string }>;
  /** When true, the legend includes "Current: N" per zone. */
  includeNowRecalls: boolean;
  /** When true, the legend includes "At creation: N" per zone. */
  includeCreationRecalls: boolean;
  /**
   * zone_id → assigned tenant name. When provided, each row says whether the
   * zone is available or assigned and the strip opens with an availability key
   * matching the map's polygon styling. Omit to leave availability off the PNG.
   */
  tenantByZone?: Record<string, string>;
  /** When false, assigned rows say only "Assigned" — the PNG must not leak tenant names. */
  includeTenantNames?: boolean;
  /**
   * When true, the legend prepends an explanatory note about the dot-density
   * layer ("each dot is a dealer; size scales with recall count"). Should
   * mirror the page's actual `showRecallDensity` state so the artifact
   * self-describes the visualization the viewer is looking at.
   */
  includeRecallDensity?: boolean;
  /** Optional human label rendered in the footer (tenant name, user email, etc.). */
  attribution?: string;
}

const LEGEND_ROW_HEIGHT = 28;
const LEGEND_PADDING = 16;
const FOOTER_HEIGHT = 32;
const EXPORT_SCALE = 2;

function buildLegendNode(opts: ExportPngOptions): HTMLDivElement {
  const {
    zones,
    recallCountsNow,
    crawlDatesNow,
    includeNowRecalls,
    includeCreationRecalls,
    includeRecallDensity,
    tenantByZone,
    includeTenantNames,
    attribution
  } = opts;
  const today = new Date().toISOString().slice(0, 10);

  // Oldest crawl cutoff among the exported zones — see crawlDatesNow. Only rendered when
  // recall numbers are actually on the artifact; a zones-only map has no data date to
  // misread. Falls back to silence rather than to `today`, which would be the exact
  // overstatement this is here to prevent.
  const dataAsOf = includeNowRecalls
    ? zones
        .map(z => crawlDatesNow?.[z.id]?.latest ?? z.latest_snapshot_date)
        .filter((d): d is string => !!d)
        .sort()[0]
    : undefined;

  const node = document.createElement('div');
  // Off-screen but laid out so html2canvas can measure it.
  node.style.cssText = `
    position: fixed;
    left: -10000px;
    top: 0;
    width: 1280px;
    background: #ffffff;
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    color: #0f172a;
    padding: ${LEGEND_PADDING}px 24px;
    box-sizing: border-box;
    border-top: 1px solid #e2e8f0;
  `;

  const rowsHtml = zones
    .map(zone => {
      const tenantName = tenantByZone?.[zone.id];
      const swatch = tenantName
        ? { fill: ASSIGNED_ZONE_COLOR.fill, stroke: ASSIGNED_ZONE_COLOR.stroke, dash: 'dashed' }
        : { ...getOemHexColor(zone.oem), dash: 'solid' };
      const recallsAtCreation = zone.total_recalls_at_creation ?? 0;
      const zipCount = zone.zip_code_count ?? zone.zip_codes?.length ?? 0;
      const recallsNow = recallCountsNow?.[zone.id];
      // Size describes the zone now, not when it was drawn. 'customer' because this PNG is
      // the artifact that gets handed to a dealership — the ladder caps at Large.
      const sizeLabel = getZoneSizeLabel(
        recallsNow ?? zone.total_recalls_current ?? 0,
        zone.oem,
        'customer'
      );

      const stats: string[] = [];
      if (tenantByZone) {
        stats.push(availabilityHtml(tenantName, !!includeTenantNames));
      }
      stats.push(`${sizeLabel}`, `${zipCount} ZIP${zipCount === 1 ? '' : 's'}`);
      if (includeNowRecalls && recallsNow !== undefined) {
        stats.push(`Current: <strong>${recallsNow.toLocaleString()}</strong>`);
      }
      if (includeCreationRecalls) {
        stats.push(`At creation: <strong>${recallsAtCreation.toLocaleString()}</strong>`);
      }

      return `
        <div style="display:flex;align-items:center;gap:10px;height:${LEGEND_ROW_HEIGHT}px;font-size:13px;">
          <span style="display:inline-block;width:14px;height:14px;border-radius:3px;background:${swatch.fill};border:1.5px ${swatch.dash} ${swatch.stroke};flex-shrink:0;"></span>
          <span style="font-weight:600;min-width:180px;">${escapeHtml(zone.zone_name || 'Zone')}</span>
          <span style="color:#64748b;">${stats.join(' · ')}</span>
        </div>
      `;
    })
    .join('');

  const footerParts = [
    `Generated ${today}`,
    ...(dataAsOf ? [`recall data as of ${dataAsOf}`] : []),
    'Vehix',
    ...(attribution ? [escapeHtml(attribution)] : [])
  ];
  const footerText = footerParts.join(' · ');

  // Density note explains the dot layer when present, so a customer viewing
  // the PNG without the live page context still understands the
  // visualization. Only emitted when the page actually rendered the layer.
  const densityNoteHtml = includeRecallDensity
    ? `<div style="
        font-size: 11px;
        color: #475569;
        margin-bottom: 8px;
        padding-bottom: 8px;
        border-bottom: 1px dashed #e2e8f0;
      ">● Dot density shows recall pressure (one dot per dealer; size scales with recall count)</div>`
    : '';

  // Availability key — decodes the warm-vs-slate polygons in the captured map. The
  // on-map key is skipped by the capture so it doesn't appear twice.
  const availableSwatch = keySwatch(AVAILABLE_ZONE_COLOR.fill, '1px solid #475569');
  const assignedSwatch = keySwatch(
    `${ASSIGNED_ZONE_COLOR.fill}66`,
    `1px dashed ${ASSIGNED_ZONE_COLOR.stroke}`
  );
  const availabilityKeyHtml = tenantByZone
    ? `<div style="
        display: flex;
        gap: 16px;
        font-size: 11px;
        color: #475569;
        margin-bottom: 8px;
        padding-bottom: 8px;
        border-bottom: 1px dashed #e2e8f0;
      ">
        <span>${availableSwatch}Available</span>
        <span>${assignedSwatch}Assigned</span>
      </div>`
    : '';

  node.innerHTML = `
    ${availabilityKeyHtml}
    ${densityNoteHtml}
    <div>${rowsHtml}</div>
    <div style="
      margin-top: 12px;
      padding-top: 10px;
      border-top: 1px solid #e2e8f0;
      font-size: 11px;
      color: #64748b;
      height: ${FOOTER_HEIGHT}px;
      box-sizing: border-box;
    ">${footerText}</div>
  `;

  document.body.appendChild(node);
  return node;
}

function keySwatch(background: string, border: string): string {
  const box = 'display:inline-block;width:10px;height:10px;border-radius:2px;';
  const place = 'vertical-align:middle;margin-right:6px;';
  return `<span style="${box}${place}background:${background};border:${border};"></span>`;
}

/** "Available", or "Assigned" with the tenant's name only when names are allowed on the PNG. */
function availabilityHtml(tenantName: string | undefined, includeTenantName: boolean): string {
  const { background, color } = ZONE_AVAILABILITY_BADGE[tenantName ? 'assigned' : 'available'];
  const pill = 'display:inline-block;padding:1px 8px;border-radius:999px;font-size:11.5px;';
  const badge = `<span style="${pill}font-weight:600;background:${background};color:${color};">${
    tenantName ? 'Assigned' : 'Available'
  }</span>`;
  const tenant =
    tenantName && includeTenantName
      ? ` <span style="color:#475569;">${escapeHtml(tenantName)}</span>`
      : '';
  return `${badge}${tenant}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function useMapPresentationExport() {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const exportPng = useCallback(
    async (opts: ExportPngOptions) => {
      if (opts.zones.length === 0) {
        toast({
          title: 'No zones to export',
          description: 'Select one or more zones, or zoom to a region with zones.',
          variant: 'destructive'
        });
        return;
      }

      const mapEl = document.getElementById('zone-map-leaflet-root');
      if (!mapEl) {
        toast({
          title: 'Export failed',
          description: 'Map element not found.',
          variant: 'destructive'
        });
        return;
      }

      setIsExporting(true);
      let legendNode: HTMLDivElement | null = null;
      try {
        // 1. Capture the live map viewport. Suppress the floating filter
        //    overlay so the PNG is a clean map; it's identifiable by its id.
        const mapCanvas = await html2canvas(mapEl, {
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          scale: EXPORT_SCALE,
          logging: false,
          ignoreElements: el => el instanceof HTMLElement && el.dataset['exportIgnore'] === 'true'
        });

        // 2. Build and capture the legend strip at the same horizontal width
        //    as the map. Match width so the composite stacks cleanly.
        legendNode = buildLegendNode(opts);
        // Force the legend to match the map's CSS width before capture so the
        // rendered canvas dimensions line up.
        legendNode.style.width = `${mapEl.clientWidth}px`;
        const legendCanvas = await html2canvas(legendNode, {
          backgroundColor: '#ffffff',
          scale: EXPORT_SCALE,
          logging: false
        });

        // 3. Composite onto a single canvas: map on top, legend below.
        const composite = document.createElement('canvas');
        composite.width = mapCanvas.width;
        composite.height = mapCanvas.height + legendCanvas.height;
        const ctx = composite.getContext('2d');
        if (!ctx) {
          throw new Error('Could not get 2D rendering context for composite canvas.');
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, composite.width, composite.height);
        ctx.drawImage(mapCanvas, 0, 0);
        ctx.drawImage(legendCanvas, 0, mapCanvas.height);

        // 4. Convert to PNG blob and trigger download.
        const blob = await new Promise<Blob | null>(resolve =>
          composite.toBlob(b => resolve(b), 'image/png')
        );
        if (!blob) {
          throw new Error('Could not convert composite canvas to PNG.');
        }
        const today = new Date().toISOString().slice(0, 10);
        const filename = `vehix-zones-${today}-${opts.zones.length}zones.png`;
        downloadBlob(blob, filename);

        toast({
          title: 'PNG ready',
          description: `Saved ${opts.zones.length} zone${
            opts.zones.length === 1 ? '' : 's'
          } to your downloads.`
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        toast({
          title: 'Export failed',
          description: message,
          variant: 'destructive'
        });
      } finally {
        if (legendNode && legendNode.parentNode) {
          legendNode.parentNode.removeChild(legendNode);
        }
        setIsExporting(false);
      }
    },
    [toast]
  );

  return { exportPng, isExporting };
}
